// engine
import {ArBitmapText, ArContainer, ArText, currencyService, gameDispatcher, ORIENTATION} from '@aceroll/core';

// library
import { ArPopup } from '@aceroll/core/library';

const featureExitPopupConfig = {
    label: 'feature-exit-popup',
    background: {
        label: 'feature-exit-popup-background',
        baseSize: {
            [ORIENTATION.LANDSCAPE]: {
                width: 2048,
                height: 945
            },
            [ORIENTATION.PORTRAIT]: {
                width: 945,
                height: 2048
            }
        },
        spine: {
            src: "spine-feature_exit_popup_background",
            animations: {
                show_landscape: 'appear_L',
                fireworks_landscape: 'idle_1_L',
                idle_landscape: 'idle_L',
                hide_landscape: 'out_L',
                show_portrait: 'appear_P',
                fireworks_portrait: 'idle_1_P',
                idle_portrait: 'idle_P',
                hide_portrait: 'out_P'
            }
        }
    },
    content: {
        label: 'feature-exit-popup-content',
        baseSize: {
            [ORIENTATION.LANDSCAPE]: {
                width: 604,
                height: 748
            },
            [ORIENTATION.PORTRAIT]: {
                width: 1040,
                height: 920
            },
        },
        baseAnchor: {
            [ORIENTATION.LANDSCAPE]: {
                x: 0.5,
                y: 0.5
            },
            [ORIENTATION.PORTRAIT]: {
                x: 0.5,
                y: 0.5
            }
        },
        text: {},
        spine: {
            src: 'spine-feature_exit_popup_content',
            animations: {
                show_landscape: 'appear_L',
                fireworks_landscape: 'idle_1_L',
                idle_landscape: 'idle_L',
                hide_landscape: 'out_L',
                show_portrait: 'appear_P',
                fireworks_portrait: 'idle_1_P',
                idle_portrait: 'idle_P',
                hide_portrait: 'out_P'
            }
        }
    },
    sounds: {
        show: null,
        idle: 'feature_exit_popup_idle',
        hide: null
    },
    behaviour: {
        autoProceedTime: 30000,
        skipOnClick: true
    }
};

const FEATURE_EXIT_CONTENT_SKIN = {
  ENGLISH: 'english',
  SPANISH: 'spanish',
};

export class FeatureExitPopup extends ArPopup {
    started = false;

    #amountText = null;

    #animations = {
        show: null,
        fireworks: null,
        idle: null,
        hide: null
    };

    constructor() {
        super(featureExitPopupConfig);
        this.setup();

        window.featureExitShow = this.show.bind(this);
        window.featureExitHide = this.hide.bind(this);
    }

    setup() {
        super.setup();

        this.#setupContentSpineSlots();
        this.#setupContentSpineSkin();
    }

    async show(amount, autoProceed) {
        super.show();

        this.#amountText.text = currencyService.toMoney(amount);

        this.visible = true;

        await Promise.all([
            this.backgroundSpine.setImmediately(this.#animations.show),
            this.contentSpine.setImmediately(this.#animations.show)
        ]);

        this.started = true;

        this.backgroundSpine.addImmediately([this.#animations.fireworks, this.#animations.idle], true);
        this.contentSpine.addImmediately([this.#animations.fireworks, this.#animations.idle], true);

        return this.proceed(autoProceed);
    }

    async hide() {
        this.started = false;

        await Promise.all([
            this.contentSpine.setImmediately(this.#animations.hide),
            this.backgroundSpine.setImmediately(this.#animations.hide)
        ]);

        this.visible = false;
    }

    onLandscape() {
        const { animations } = this.config.background.spine;

        this.#animations.show = animations.show_landscape;
        this.#animations.fireworks = animations.fireworks_landscape;
        this.#animations.idle = animations.idle_landscape;
        this.#animations.hide = animations.hide_landscape;

        this.backgroundSpine.position.set(1024, 472);
        this.contentSpine.position.set(302, 260);

        if (this.started) {
            this.backgroundSpine.stop(true);
            this.contentSpine.stop(true);

            this.backgroundSpine.setImmediately(this.#animations.idle, true);
            this.contentSpine.setImmediately(this.#animations.idle, true);
        }

        this.#amountText.setScalable(900);
    }

    onPortrait() {
        const { animations } = this.config.background.spine;

        this.#animations.show = animations.show_portrait;
        this.#animations.fireworks = animations.fireworks_portrait;
        this.#animations.idle = animations.idle_portrait;
        this.#animations.hide = animations.hide_portrait;

        this.backgroundSpine.position.set(472, 1024);
        this.contentSpine.position.set(520, 160);

        if (this.started) {
            this.backgroundSpine.stop(true);
            this.contentSpine.stop(true);

            this.backgroundSpine.setImmediately(this.#animations.idle, true);
            this.contentSpine.setImmediately(this.#animations.idle, true);
        }

        this.#amountText.setScalable(1000);
    }

  #setupContentSpineSlots() {
    this.#amountText = ArBitmapText.from('bitmap-total_win', '', 'amount-text', {
      fontSize: 32
    });

    const amountTextContainer = new ArContainer('amount-text-container');
    amountTextContainer.addChild(this.#amountText);

    this.#amountText.anchor.set(0.5);

    const continueText = ArText.from('click_to_continue_uc', 'featureExitSkip', 'click-to-continue-text');
    continueText.anchor.set(0.5);

    this.contentSpine.addToSlot(amountTextContainer, 'text_holder');
    this.contentSpine.addToSlot(continueText, 'continue_text_holder');
  }

  #setupContentSpineSkin() {
    const { localeCode } = gameDispatcher.state;

    const skinToUse = localeCode === 'es-es' ? FEATURE_EXIT_CONTENT_SKIN.SPANISH : FEATURE_EXIT_CONTENT_SKIN.ENGLISH;
    this.contentSpine.setSkin(skinToUse);
  }
}
